import { Box, Divider } from "@mui/material";

import { fmtCurrencyExact, fmtPct } from "./formatters";
import { CardTitle, HeroStat } from "./primitives";

const YTD_SUMMARY_HELP_TEXT = {
  marketedPremium: "Total premium sent to carriers for quotes this year.",
  finalizedPremium: "Premium for groups with a final carrier choice.",
  activeOtmPremium: "Premium for groups that are still out to market.",
  migrationSuccess: "Premium that started with a non-preferred carrier.",
  retentionSuccess: "Premium that started with a Tier 1 or Tier 2 carrier.",
  pendingOutcomesPremium:
    "Premium for groups with quotes but no final carrier choice yet.",
  newLinePremium: "Premium for new lines of coverage with no prior carrier.",
  movedToStrategic:
    "Premium moved from a non-preferred carrier to a Tier 1 or Tier 2 carrier.",
  retainedWithStrategic: "Premium that stayed with a Tier 1 or Tier 2 carrier.",
  strategicShare: "Share of managed premium with a Tier 1 or Tier 2 carrier.",
};

type YtdSummaryCardProps = {
  marketedPremium: number;
  finalizedPremium: number;
  premiumOutToMarket: number;
  migrationSuccessPremium: number;
  retentionSuccessPremium: number;
  pendingOutcomesPremium: number;
  hidePendingOutcomesPremium?: boolean;
  newLinePremium: number;
  movedToStrategicPremium: number;
  retainedWithStrategicPremium: number;
  strategicSharePercent: number;
};

function YtdSummaryCard({
  marketedPremium,
  finalizedPremium,
  premiumOutToMarket,
  migrationSuccessPremium,
  retentionSuccessPremium,
  pendingOutcomesPremium,
  hidePendingOutcomesPremium = false,
  newLinePremium,
  movedToStrategicPremium,
  retainedWithStrategicPremium,
  strategicSharePercent,
}: YtdSummaryCardProps): React.JSX.Element {
  return (
    <Box data-testid="summary-card">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <CardTitle>Summary</CardTitle>
      </Box>
      <HeroStat
        label="Marketed Premium"
        value={fmtCurrencyExact(marketedPremium)}
        description={YTD_SUMMARY_HELP_TEXT.marketedPremium}
      />
      <Divider sx={{ my: 1 }} />
      <HeroStat
        label="Active OTM Premium"
        value={fmtCurrencyExact(premiumOutToMarket)}
        description={YTD_SUMMARY_HELP_TEXT.activeOtmPremium}
      />
      <Divider sx={{ my: 1 }} />
      <HeroStat
        label="Migration Success"
        value={fmtCurrencyExact(migrationSuccessPremium)}
        description={YTD_SUMMARY_HELP_TEXT.migrationSuccess}
      />
      <Divider sx={{ my: 1 }} />
      <HeroStat
        label="Retention Success"
        value={fmtCurrencyExact(retentionSuccessPremium)}
        description={YTD_SUMMARY_HELP_TEXT.retentionSuccess}
      />
      <Divider sx={{ my: 1 }} />
      <HeroStat
        label="Cross Sell Success"
        value={fmtCurrencyExact(newLinePremium)}
        description={YTD_SUMMARY_HELP_TEXT.newLinePremium}
      />
      <Divider sx={{ my: 1 }} />
      <HeroStat
        label="Finalized Premium"
        value={fmtCurrencyExact(finalizedPremium)}
        description={YTD_SUMMARY_HELP_TEXT.finalizedPremium}
      />
      {!hidePendingOutcomesPremium && (
        <>
          <Divider sx={{ my: 1 }} />
          <HeroStat
            label="Pending Outcomes"
            value={fmtCurrencyExact(pendingOutcomesPremium)}
            description={YTD_SUMMARY_HELP_TEXT.pendingOutcomesPremium}
          />
        </>
      )}
      <Divider sx={{ my: 1 }} />
      <HeroStat
        label="Moved to Strategic Carrier"
        value={fmtCurrencyExact(movedToStrategicPremium)}
        description={YTD_SUMMARY_HELP_TEXT.movedToStrategic}
      />
      <Divider sx={{ my: 1 }} />
      <HeroStat
        label="Retained with Strategic Carrier"
        value={fmtCurrencyExact(retainedWithStrategicPremium)}
        description={YTD_SUMMARY_HELP_TEXT.retainedWithStrategic}
      />
      <Divider sx={{ my: 1 }} />
      <HeroStat
        label="Strategic Carrier Share"
        value={fmtPct(strategicSharePercent)}
        description={YTD_SUMMARY_HELP_TEXT.strategicShare}
      />
    </Box>
  );
}

export { YtdSummaryCard };
export type { YtdSummaryCardProps };
